/**
 * GlassModal Component
 * Glassmorphic modal extending shadcn/ui Dialog
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const GlassModal = DialogPrimitive.Root;

const GlassModalTrigger = DialogPrimitive.Trigger;

const GlassModalPortal = DialogPrimitive.Portal;

const GlassModalClose = DialogPrimitive.Close;

/**
 * Semi-transparent overlay with blur
 */
const GlassModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50',
      // Semi-transparent with blur
      'bg-black/50',
      'backdrop-blur-sm',
      // Animations
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
GlassModalOverlay.displayName = 'GlassModalOverlay';

export interface GlassModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Handler for close button click (allows interception for confirmation) */
  onCloseClick?: (e: React.MouseEvent) => void;
}

/**
 * Glassmorphic modal content
 */
const GlassModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  GlassModalContentProps
>(({ className, children, showCloseButton = true, onCloseClick, ...props }, ref) => (
  <GlassModalPortal>
    <GlassModalOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50',
        'w-[calc(100%-2rem)] max-w-lg',
        'translate-x-[-50%] translate-y-[-50%]',
        'grid gap-4 p-6',
        // Glassmorphic styles
        'bg-white',
        'backdrop-blur-[calc(var(--glass-blur)*1.5)]',
        '[-webkit-backdrop-filter:blur(calc(var(--glass-blur)*1.5))]',
        'border border-[var(--glass-border)]',
        'rounded-[var(--radius-lg)]',
        'shadow-[var(--glass-shadow)]',
        // Animations
        'duration-200',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          className={cn(
            'absolute right-4 top-4',
            'rounded-sm opacity-70',
            'ring-offset-background',
            'transition-opacity',
            'hover:opacity-100',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-start)] focus:ring-offset-2',
            'disabled:pointer-events-none'
          )}
          onClick={onCloseClick}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </GlassModalPortal>
));
GlassModalContent.displayName = 'GlassModalContent';

/**
 * Modal header section
 */
const GlassModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
GlassModalHeader.displayName = 'GlassModalHeader';

/**
 * Modal footer section
 */
const GlassModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
GlassModalFooter.displayName = 'GlassModalFooter';

/**
 * Modal title
 */
const GlassModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      'text-[var(--color-charcoal)]',
      className
    )}
    {...props}
  />
));
GlassModalTitle.displayName = 'GlassModalTitle';

/**
 * Modal description
 */
const GlassModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-[var(--color-charcoal)]/70', className)}
    {...props}
  />
));
GlassModalDescription.displayName = 'GlassModalDescription';

export {
  GlassModal,
  GlassModalPortal,
  GlassModalOverlay,
  GlassModalClose,
  GlassModalTrigger,
  GlassModalContent,
  GlassModalHeader,
  GlassModalFooter,
  GlassModalTitle,
  GlassModalDescription,
};
